import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";

import { GridHeader } from "../GridHeader";

import { ScheduleField, ScheduleSchedule } from "@/components/list/Schedule/types";
import storageManager from "@/lib/storageManager";

type Props = {
  field: ScheduleField;
  data: ScheduleSchedule;
  editableSekou: boolean;
  editableSeiyu: boolean;
  bulkEditTextItem: (label: string, target: string, options?: { multiline?: boolean }) => void;
  bulkEditSelectItem: (label: string, target: string, optionKey?: string) => void;
  lastColumn?: boolean;
};

export const ConfigurablePlanListHeaderColumn = ({
  field,
  data,
  editableSekou,
  editableSeiyu,
  bulkEditTextItem,
  bulkEditSelectItem,
  lastColumn = false,
}: Props) => {
  const { t } = useTranslation();
  const fieldKey = field.field_key;
  const locale = storageManager.getLocale();
  const label = locale === "ja" ? field.field_label_jp : field.field_label_en;

  if (fieldKey === "field3") {
    return (
      <GridHeader
        className={clsx("col5", lastColumn && "last-column")}
        editable={data?.roles.field3 && editableSekou}
        onClick={() => bulkEditTextItem(label || t("construction_attend") + t("company_title"), "field3")}
        data-test-id="schedule-header-field3"
      >
        {label || (
          <>
            {t("construction_attend")}
            <br />
            {t("company_title")}
          </>
        )}
      </GridHeader>
    );
  }
  if (fieldKey === "field_t1") {
    return (
      <GridHeader
        className={clsx("col6", !label && "vertical-text", lastColumn && "last-column")}
        editable={data?.roles.field_t1 && editableSekou}
        onClick={() => bulkEditTextItem(label || t("number_of_workers"), "field_t1")}
        data-test-id="schedule-header-field-t1"
      >
        {label || t("number_of_workers")}
      </GridHeader>
    );
  }
  if (fieldKey === "field_t9") {
    return (
      <GridHeader
        className={clsx("col6", !label && "vertical-text", lastColumn && "last-column")}
        editable={data?.roles.field_t9 && editableSekou}
        onClick={() => bulkEditTextItem(label || t("overtime_schedule"), "field_t9")}
        data-test-id="schedule-header-field-t9"
      >
        {label || t("overtime_schedule")}
      </GridHeader>
    );
  }
  if (fieldKey === "field_t2") {
    return (
      <GridHeader
        className={clsx("col7", !label && "vertical-text", lastColumn && "last-column")}
        editable={data?.roles.field_t2 && editableSekou}
        onClick={() => bulkEditSelectItem(label || `${t("fire")} : ${t("open_flame")}`, "field_t2")}
        data-test-id="schedule-header-field-t2"
      >
        {label || t("open_flame")}
      </GridHeader>
    );
  }
  if (fieldKey === "field_t3") {
    return (
      <GridHeader
        className={clsx("col8", lastColumn && "last-column")}
        editable={data?.roles.field_t3 && editableSekou}
        onClick={() => bulkEditSelectItem(label || `${t("fire")} : ${t("usual")}${t("fire")}`, "field_t3")}
        data-test-id="schedule-header-field-t3"
      >
        {label || (
          <>
            {t("usual")}
            <br />
            {t("fire")}
          </>
        )}
      </GridHeader>
    );
  }
  if (fieldKey === "field_t4") {
    return (
      <GridHeader
        className={clsx("col9", lastColumn && "last-column")}
        editable={data?.roles.field_t4 && editableSekou}
        onClick={() => bulkEditTextItem(label || `${t("fire")} : ${t("use_flame")}`, "field_t4")}
        data-test-id="schedule-header-field-t4"
      >
        {label || t("use_flame")}
      </GridHeader>
    );
  }
  if (fieldKey === "field_t5") {
    return (
      <GridHeader
        className={clsx("col10", lastColumn && "last-column")}
        editable={data?.roles.field_t5 && editableSekou}
        onClick={() => bulkEditSelectItem(label || t("pipe") + t("cutting"), "field_t5")}
        data-test-id="schedule-header-field-t5"
      >
        {label || (
          <>
            {t("pipe")}
            <br />
            {t("cutting")}
          </>
        )}
      </GridHeader>
    );
  }
  if (fieldKey === "field_t7") {
    return (
      <GridHeader
        className={clsx("col11", lastColumn && "last-column")}
        editable={data?.roles.field_t7 && editableSekou}
        onClick={() => bulkEditTextItem(label || t("heavy_machine"), "field_t7")}
        data-test-id="schedule-header-field-t7"
      >
        {label || t("heavy_machine")}
      </GridHeader>
    );
  }
  if (fieldKey === "field_t6") {
    return (
      <GridHeader
        className={clsx("col12", !label && "vertical-text", lastColumn && "last-column")}
        editable={data?.roles.field_t6 && editableSekou}
        onClick={() => bulkEditSelectItem(label || t("lack_of_oxygen"), "field_t6")}
        data-test-id="schedule-header-field-t6"
      >
        {label || t("lack_of_oxygen")}
      </GridHeader>
    );
  }
  if (fieldKey === "field1") {
    return (
      <GridHeader
        className={clsx("col13", lastColumn && "last-column")}
        editable={data?.roles.field1 && editableSekou}
        onClick={() => bulkEditSelectItem(label || t("hazard") + t("rank"), "field1")}
        data-test-id="schedule-header-field1"
      >
        {label || (
          <>
            {t("hazard")}
            <br />
            {t("rank")}
          </>
        )}
      </GridHeader>
    );
  }
  if (fieldKey === "field2") {
    return (
      <GridHeader
        className={clsx("col14", lastColumn && "last-column")}
        editable={data?.roles.field2 && editableSeiyu}
        underColor="green"
        onClick={() => bulkEditSelectItem(label || t("attendance"), "field2")}
        data-test-id="schedule-header-field2"
      >
        {label || t("attendance")}
      </GridHeader>
    );
  }
  if (fieldKey === "field_t8") {
    return (
      <GridHeader
        className={clsx("col15", lastColumn && "last-column")}
        editable={data?.roles.field_t8 && editableSekou}
        onClick={() => bulkEditTextItem(label || t("safety") + t("instruction"), "field_t8")}
        data-test-id="schedule-header-field-t8"
      >
        {label || (
          <>
            {t("safety")}
            <br />
            {t("instruction")}
          </>
        )}
      </GridHeader>
    );
  }
  if (fieldKey === "field4") {
    return (
      <GridHeader
        className={clsx("col16", lastColumn && "last-column")}
        editable={data?.roles.field4 && editableSekou}
        onClick={() =>
          bulkEditTextItem(`${t("safety_instruction_detail")} : ${label || t("hazard_source")}`, "field4", {
            multiline: true,
          })
        }
        data-test-id="schedule-header-field4"
      >
        {label || t("hazard_source")}
      </GridHeader>
    );
  }
  if (fieldKey === "field5") {
    return (
      <GridHeader
        className={clsx("col17", lastColumn && "last-column")}
        editable={data?.roles.field5 && editableSekou}
        style={{ borderRight: "1px solid #fff" }}
        onClick={() =>
          bulkEditTextItem(`${t("safety_instruction_detail")} : ${label || t("hazard_reduction_activity")}`, "field5", {
            multiline: true,
          })
        }
        data-test-id="schedule-header-field5"
      >
        {label || t("hazard_reduction_activity")}
      </GridHeader>
    );
  }
  if (fieldKey === "note3") {
    return (
      <GridHeader
        className={clsx("w-[120px] min-w-[120px]", lastColumn && "last-column")}
        editable={!!data?.roles.note3}
        onClick={() =>
          bulkEditTextItem(label || `${t("primary_charge")}${t("comments")}`, "note3", {
            multiline: true,
          })
        }
        data-test-id="schedule-header-note3"
      >
        {label || (
          <>
            {t("primary_charge")}
            <br />
            {t("comments")}
          </>
        )}
      </GridHeader>
    );
  }
  if (fieldKey === "approve1") {
    return (
      <GridHeader
        className={clsx("col18", lastColumn && "last-column")}
        editable={data?.roles.approve1 && editableSeiyu}
        underColor="red"
        onClick={() =>
          bulkEditSelectItem(label || t("construction") + t("supervising_section"), "approve1_status", "approve1")
        }
        data-test-id="schedule-header-approve1"
      >
        {label || (
          <>
            {t("construction")}
            <br />
            {t("supervising_section")}
          </>
        )}
      </GridHeader>
    );
  }
  if (fieldKey === "approve2") {
    return (
      <GridHeader
        className={clsx("col19", lastColumn && "last-column")}
        editable={data?.roles.approve2 && editableSeiyu}
        underColor="red"
        onClick={() => bulkEditSelectItem(label || t("PL"), "approve2_status", "approve2")}
        data-test-id="schedule-header-approve2"
      >
        {label || "PL"}
      </GridHeader>
    );
  }
  if (fieldKey === "approve3") {
    return (
      <GridHeader
        className={clsx("col20", lastColumn && "last-column")}
        editable={data?.roles.approve3 && editableSeiyu}
        underColor="red"
        onClick={() => bulkEditSelectItem(label || t("TL"), "approve3_status", "approve3")}
        data-test-id="schedule-header-approve3"
      >
        {label || "TL"}
      </GridHeader>
    );
  }
  if (fieldKey === "note1") {
    return (
      <GridHeader
        className={clsx("col21", lastColumn && "last-column")}
        editable={!!data?.roles.note1}
        underColor="red"
        onClick={() =>
          bulkEditTextItem(label || `${t("construction")}${t("supervising_section")}${t("comments")}`, "note1", {
            multiline: true,
          })
        }
        data-test-id="schedule-header-note1"
      >
        {label || (
          <>
            {t("construction")}
            <br />
            {t("supervising_section")}
            <br />
            {t("comments")}
          </>
        )}
      </GridHeader>
    );
  }
  if (fieldKey === "approve4") {
    return (
      <GridHeader
        className={clsx("col22", lastColumn && "last-column")}
        editable={data?.roles.approve4 && editableSeiyu}
        underColor="green"
        onClick={() =>
          bulkEditSelectItem(label || t("facility") + t("supervising_section"), "approve4_status", "approve4")
        }
        data-test-id="schedule-header-approve4"
      >
        {label || (
          <>
            {t("facility")}
            <br />
            {t("supervising_section")}
          </>
        )}
      </GridHeader>
    );
  }
  if (fieldKey === "note2") {
    return (
      <GridHeader
        className={clsx("col23", lastColumn && "last-column")}
        editable={!!data?.roles.note2}
        underColor="green"
        onClick={() =>
          bulkEditTextItem(label || `${t("facility")}${t("supervising_section")}${t("comments")}`, "note2", {
            multiline: true,
          })
        }
        data-test-id="schedule-header-note2"
      >
        {label || (
          <>
            {t("facility")}
            {t("supervising_section")}
            <br />
            {t("comments")}
          </>
        )}
      </GridHeader>
    );
  }

  return <></>;
};
